import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

const AboutUs = () => {
  const valores = [
    {
      icon: "ShieldCheck",
      title: "Seriedad",
      description: "Comprometidos con la transparencia y profesionalismo en cada interacción"
    },
    {
      icon: "Award",
      title: "Responsabilidad",
      description: "Garantizamos el cumplimiento de nuestros compromisos con usuarios y empresas"
    },
    {
      icon: "CheckCircle2",
      title: "Cumplimiento",
      description: "Entregamos resultados consistentes y confiables en todas nuestras operaciones"
    },
    {
      icon: "Eye",
      title: "Transparencia",
      description: "Operamos con claridad y honestidad en todas nuestras prácticas comerciales"
    }
  ];

  const objetivosEstrategicos = [
    {
      icon: "Rocket",
      title: "Plataforma Innovadora",
      description: "Desarrollar y mantener una plataforma de descuentos innovadora y escalable"
    },
    {
      icon: "Handshake",
      title: "Alianzas Estratégicas",
      description: "Establecer alianzas estratégicas con locales comerciales de calidad"
    },
    {
      icon: "Users",
      title: "Base de Clientes",
      description: "Incrementar la base de clientes y fortalecer la lealtad de marca"
    },
    {
      icon: "Globe",
      title: "Posicionamiento",
      description: "Posicionar nuestra aplicación en el mercado nacional e internacional"
    }
  ];

  const objetivosTacticos = [
    {
      icon: "Calendar",
      metric: "2 meses",
      description: "Lanzar nuestra aplicación en los primeros 2 meses"
    },
    {
      icon: "Store",
      metric: "100 alianzas",
      description: "Establecer 100 alianzas con locales comerciales en el primer mes"
    },
    {
      icon: "TrendingUp",
      metric: "10,000 clientes",
      description: "Atraer 10,000 clientes en los primeros 12 meses"
    },
    {
      icon: "MapPin",
      metric: "3 países",
      description: "Expandir nuestra presencia en 3 países en los próximos 2 años"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Quiénes Somos - BIZZNET</title>
        <meta name="description" content="Conoce más sobre BIZZNET, nuestra misión, visión, valores y objetivos para revolucionar el comercio digital en Colombia y Latinoamérica." />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero Section - FIXED COLOR CONTRAST */}
        <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Link to="/" className="text-white/90 hover:text-white transition-colors drop-shadow-md">
                <Icon name="ArrowLeft" size={24} />
              </Link>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">BIZZNET</h1>
                <p className="text-white/90 drop-shadow-md">Fundada en 2025</p>
              </div>
            </div>
            <p className="text-xl md:text-2xl font-medium max-w-3xl text-white/95 drop-shadow-md">
              Conectamos a usuarios y empresas en una comunidad comercial dinámica
            </p>
          </div>
        </section>

        {/* Quiénes Somos */}
        <section className="py-8 px-6 bg-card">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Icon name="Building2" size={32} className="text-primary" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Quiénes Somos</h2>
            </div>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-4xl">
              En BIZZNET, conectamos a usuarios y empresas en una comunidad comercial dinámica. Nuestra plataforma permite a los usuarios acceder a miles de descuentos exclusivos, mientras que las empresas pueden configurar perfiles profesionales, compartir sus productos y servicios, y establecer canales directos de contacto. Nuestro objetivo es impulsar el ahorro para los usuarios y el crecimiento para las marcas, creando una red de beneficios mutuos.
            </p>
          </div>
        </section>

        {/* Misión y Visión */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Misión */}
              <div className="bg-card border border-border rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 p-3 rounded-xl">
                    <Icon name="Target" size={32} className="text-primary" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-foreground">Misión</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  En BIZZNET nuestra misión es revolucionar la forma en que las personas interactúan con los locales comerciales, creando una plataforma innovadora que conecta a clientes con descuentos exclusivos y oportunidades de ahorro. Nos comprometemos a establecer alianzas estratégicas con locales comerciales para ofrecer experiencias de compra personalizadas y accesibles, impulsando el crecimiento y la lealtad de nuestros clientes.
                </p>
              </div>

              {/* Visión */}
              <div className="bg-card border border-border rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 p-3 rounded-xl">
                    <Icon name="Eye" size={32} className="text-primary" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-foreground">Visión</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Nuestra visión en BIZZNET es ser líderes en la industria de la tecnología de descuentos, creando una comunidad de clientes y locales comerciales que se beneficien mutuamente. Queremos ser la plataforma de referencia para aquellos que buscan ahorrar dinero y disfrutar de experiencias de compra únicas. Nuestro objetivo es expandir nuestra red de alianzas y llegar a ser la aplicación de descuentos más utilizada en el mercado, mientras mantenemos nuestro compromiso con la innovación y la excelencia.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Valores */}
        <section className="py-16 px-6 bg-card">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-12">
              <Icon name="Heart" size={32} className="text-primary" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Nuestros Valores</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {valores?.map((valor, index) => (
                <div 
                  key={index}
                  className="bg-background border border-border rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                    <Icon name={valor?.icon} size={28} className="text-primary" />
                  </div>
                  <h4 className="text-xl font-bold text-foreground mb-3">{valor?.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {valor?.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Sobre BIZZNET */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Icon name="Info" size={32} className="text-primary" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Sobre BIZZNET</h2>
            </div>
            <div className="bg-card border border-border rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-primary/10 p-3 rounded-xl flex-shrink-0">
                  <Icon name="Sparkles" size={28} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Nuestra Historia</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    BIZZNET LATAM fue fundado en 2025, con raíces que se remontan a 2020, cuando un equipo visionario comenzó a desarrollar una plataforma innovadora para conectar a clientes con locales comerciales a través de alianzas estratégicas.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Nuestro equipo de expertos en tecnología y comercialización ha trabajado incansablemente para crear una aplicación que no solo ofrece descuentos exclusivos, sino que también revoluciona la forma en que las personas interactúan con los negocios locales.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Creemos que la tecnología puede ser una fuerza positiva para el crecimiento y la prosperidad de las comunidades. Estamos comprometidos con hacer de nuestra plataforma una herramienta clave para ayudar a nuestros clientes y a los negocios a crecer juntos.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Objetivos Estratégicos */}
        <section className="py-16 px-6 bg-card">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-12">
              <Icon name="Flag" size={32} className="text-primary" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Objetivos Estratégicos</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {objetivosEstrategicos?.map((objetivo, index) => (
                <div 
                  key={index}
                  className="bg-background border border-border rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                    <Icon name={objetivo?.icon} size={28} className="text-primary" />
                  </div>
                  <h4 className="text-lg font-bold text-foreground mb-3">{objetivo?.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {objetivo?.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Objetivos Tácticos */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-12">
              <Icon name="Zap" size={32} className="text-primary" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Objetivos Tácticos</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {objetivosTacticos?.map((objetivo, index) => (
                <div 
                  key={index}
                  className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow text-center"
                >
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name={objetivo?.icon} size={32} className="text-primary" />
                  </div>
                  <div className="text-3xl font-bold text-primary mb-3">{objetivo?.metric}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {objetivo?.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section - ALSO FIXED FOR CONSISTENCY */}
        <section className="py-16 px-6 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white drop-shadow-lg">
              Únete a la Revolución del Comercio Digital
            </h2>
            <p className="text-lg mb-8 text-white/95 drop-shadow-md">
              Sé parte de la comunidad que está transformando la forma de hacer negocios en Colombia
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/user-registration"
                className="bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2 shadow-lg"
              >
                <Icon name="UserPlus" size={20} />
                Registrarse como Usuario
              </Link>
              <Link
                to="/business-registration"
                className="bg-white/10 text-white border-2 border-white/30 px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-colors inline-flex items-center justify-center gap-2 backdrop-blur-sm"
              >
                <Icon name="Store" size={20} />
                Registrar Negocio
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutUs;